/* ==========================================================================
   KAK LARNEY — theme behaviour.
   No framework, no dependencies. Everything degrades: without JS the product
   form posts normally and the cart page still works.
   ========================================================================== */

(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const money = (s) => s;

  /* ---------------------------------------------------------------- Cart */

  const drawer = $('[data-cart-drawer]');
  const drawerBody = $('[data-cart-body]');
  let lastFocus = null;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function openDrawer() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.hidden = false;
    // Next frame so the transition has a start value to animate from.
    requestAnimationFrame(() => drawer.setAttribute('data-open', 'true'));
    document.body.style.overflow = 'hidden';
    const first = $(FOCUSABLE, drawer);
    if (first) first.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.removeAttribute('data-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    const done = () => {
      drawer.hidden = true;
      drawer.removeEventListener('transitionend', done);
    };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) done();
    else drawer.addEventListener('transitionend', done);
    if (lastFocus) lastFocus.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = $$(FOCUSABLE, drawer).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  async function refreshCart() {
    if (!drawerBody) return;
    drawerBody.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.location.pathname}?section_id=cart-items`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
        const html = await res.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const next = parsed.querySelector('[data-cart-contents]');
        if (next) drawerBody.innerHTML = next.innerHTML;
      }
    } catch (_) {
      /* leave the current contents in place rather than blanking the drawer */
    }
    drawerBody.setAttribute('aria-busy', 'false');
    await syncCount();
  }

  async function syncCount() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
    } catch (_) {}
  }

  async function changeLine(line, quantity) {
    drawerBody?.setAttribute('aria-busy', 'true');
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
    } catch (_) {}
    await refreshCart();
  }

  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-cart-open]');
    if (open) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    const remove = e.target.closest('[data-remove]');
    if (remove) {
      e.preventDefault();
      changeLine(Number(remove.dataset.remove), 0);
    }
  });

  document.addEventListener('change', (e) => {
    const qty = e.target.closest('[data-qty]');
    if (!qty) return;
    const n = Math.max(0, parseInt(qty.value, 10) || 0);
    changeLine(Number(qty.dataset.line), n);
  });

  /* --------------------------------------------------- Add to cart */

  const form = $('#add-to-cart-form');
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const button = $('[data-add-to-cart]', form);
      const label = $('[data-add-label]', form);
      const prior = label ? label.textContent : '';
      if (button) button.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form)
        });
        if (!res.ok) throw new Error('add failed');
        await refreshCart();
        openDrawer();
      } catch (_) {
        // Fall back to a normal navigation rather than silently failing.
        form.submit();
        return;
      } finally {
        if (button) button.removeAttribute('aria-disabled');
        if (label) label.textContent = prior;
      }
    });
  }

  /* ------------------------------------------- Variant switching */

  const variantScript = $('[data-variant-data]');
  if (variantScript && form) {
    let variants = [];
    try {
      variants = JSON.parse(variantScript.textContent);
    } catch (_) {}

    const idInput = $('[data-variant-id]', form);
    const priceEl = $('[data-price]');
    const addLabel = $('[data-add-label]', form);
    const addBtn = $('[data-add-to-cart]', form);

    const selected = () =>
      $$('.swatch[aria-checked="true"]', form).map((b) => b.dataset.optionValue);

    function apply() {
      const chosen = selected();
      const match = variants.find(
        (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
      );
      if (!match) return;
      if (idInput) idInput.value = match.id;
      if (priceEl) priceEl.textContent = money(match.price);
      if (addBtn) addBtn.disabled = !match.available;
      if (addLabel) addLabel.textContent = match.available ? 'Add to cart' : 'Sold out';
      // Keep the URL shareable without a reload.
      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    }

    form.addEventListener('click', (e) => {
      const sw = e.target.closest('.swatch');
      if (!sw) return;
      const group = sw.closest('.swatches');
      $$('.swatch', group).forEach((b) => b.setAttribute('aria-checked', 'false'));
      sw.setAttribute('aria-checked', 'true');
      apply();
    });

    // Radio groups must be arrow-navigable to be a real radiogroup.
    form.addEventListener('keydown', (e) => {
      const sw = e.target.closest('.swatch');
      if (!sw) return;
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
      if (!keys.includes(e.key)) return;
      e.preventDefault();
      const group = [...sw.closest('.swatches').querySelectorAll('.swatch')];
      const i = group.indexOf(sw);
      const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
      const next = group[(i + dir + group.length) % group.length];
      group.forEach((b) => b.setAttribute('aria-checked', 'false'));
      next.setAttribute('aria-checked', 'true');
      next.focus();
      apply();
    });
  }

  /* --------------------------------------------------- Size guide */

  const sizeGuideLink = $('[data-size-guide]');
  const sizeGuide = $('[data-size-guide-panel]');
  if (sizeGuideLink && sizeGuide) {
    sizeGuideLink.addEventListener('click', (e) => {
      e.preventDefault();
      const open = sizeGuide.hasAttribute('hidden');
      if (open) {
        sizeGuide.removeAttribute('hidden');
        sizeGuideLink.setAttribute('aria-expanded', 'true');
        sizeGuide.focus();
      } else {
        sizeGuide.setAttribute('hidden', '');
        sizeGuideLink.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* ------------------------------------------------ Announcement bar */

  const announce = $('[data-announce]');
  if (announce) {
    try {
      if (sessionStorage.getItem('kak-announce') === 'off') announce.hidden = true;
    } catch (_) {}
    announce.addEventListener('click', (e) => {
      if (!e.target.closest('[data-announce-close]')) return;
      announce.hidden = true;
      try { sessionStorage.setItem('kak-announce', 'off'); } catch (_) {}
    });
  }

  /* ------------------------------------------------------ PDP gallery */

  const stage = $('[data-stage]');
  if (stage) {
    const stageImg = $('[data-stage-img]', stage);
    const stagePrint = $('[data-stage-print]', stage);
    $$('.pdp__thumb').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const view = thumb.dataset.view;
        const src = thumb.dataset.src;
        if (!stageImg || !src) return;
        stageImg.src = src;
        stage.dataset.view = view;
        // The live phrase belongs only on the blank front view; the folded and
        // detail renders already carry the print.
        if (stagePrint) stagePrint.hidden = view !== 'front';
        $$('.pdp__thumb').forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
      });
    });
  }
})();
